<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * M3 Badge primitive. Two implicit variants:
 *   - Small (dot): no count — 6px circle, signals "something is new"
 *   - Large: with count — 16px pill, displays the number (capped at 999+)
 *
 * Color is always error/on-error per the M3 spec.
 * By default the badge floats top-trailing over its parent (parent must have `position: relative`).
 * Pass `inline` for in-flow placement, e.g. after a label-only tab.
 *
 * Not to be confused with HrDataLabel, which uses the same pill shape to annotate UI
 * surfaces with data values (counts, durations, statuses). HrDataLabel is neutral-colored,
 * optionally floating, and optionally interactive — a different semantic concept that
 * doesn't belong in /primitives either; see src/design-system/migration/HrDataLabel.vue.
 *
 * @status recommended
 * @group status-feedback
 * @useWhen Showing unread counts or new-activity indicators over icons, avatars, or tab labels
 * @within hr-btn {"variant":"filled","label":"Inbox"}
 * @see https://quasar.dev/vue-components/badge Quasar q-badge API
 */
defineOptions({ name: 'HrBadge' });

const props = defineProps({
  /**
   * Notification count. Omit (or null) for a small dot badge.
   * Values above 999 are displayed as "999+".
   * @example 3
   */
  count: {
    type: Number,
    default: null,
  },
  /**
   * Renders the badge inline (in document flow) rather than floating top-trailing
   * over its parent. Use for label-only contexts where there is no icon to anchor to.
   */
  inline: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const isDot = computed(() => props.count === null);
const displayLabel = computed(() => {
  if (props.count === null) return null;
  return props.count > 999 ? '999+' : String(props.count);
});

const effectiveProps = computed(() => {
  const base = {
    floating: !props.inline,
    ...(displayLabel.value !== null && { label: displayLabel.value }),
  };

  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-badge
    v-bind="effectiveProps"
    :class="hasTheme ? ['hr-badge', isDot ? 'hr-badge--dot' : 'hr-badge--labeled'] : undefined"
  />
</template>

<style lang="scss" scoped>
.hr-badge {
  background: var(--app-error);
  color: var(--app-on-error);
  border-radius: var(--app-shape-corner-full);
}

.hr-badge--dot {
  width: 6px;
  min-width: 6px;
  height: 6px;
  min-height: 6px;
  padding: 0;
  font-size: 0;
  line-height: 0;
}

.hr-badge--labeled {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  min-height: 16px;
  min-width: 16px;
  padding: 0 4px;
  font-size: var(--app-type-label-small-size);
  font-weight: var(--app-type-label-small-weight);
  line-height: 1;
}

// M3 spec positioning (only active in theme mode — hr-badge--* classes are only added when hasTheme is true).
//
// Small badge: dot center at the icon's top-trailing corner.
//   For a 24dp icon centered in a 40dp touch target: corner is at (top:8, right:8) of the button.
//   Dot offset = corner - half(6px) = 5px → rounded to 6px inset.
//
// Large badge: top 2dp above icon top; left edge 12dp inside icon's trailing edge; grows rightward.
//   calc(100% - 18px) ≈ 12dp inside trailing edge for a 24dp icon in a standard button.
.hr-badge--dot.q-badge--floating {
  top: 6px;
  right: 6px;
  left: auto;
  transform: none;
}

.hr-badge--labeled.q-badge--floating {
  top: -2px;
  right: auto;
  left: calc(100% - 18px);
  transform: none;
}
</style>
